import { ScaleElement } from '@scale/base-element';

class ScaleMegamenuNav extends ScaleElement {
  #activeItem = null;
  #hoverIntent;
  #closeTimer;
  #dropdownTimers = new Map();

  setup() {
    for (const item of this.querySelectorAll('[data-has-megamenu]')) {
      const trigger = item.querySelector('[data-megamenu-trigger]');
      const panel = item.querySelector('[data-megamenu-panel]');
      if (!trigger) continue;

      trigger.addEventListener('pointerenter', () => this.#handleEnter(item), { signal: this.signal });
      trigger.addEventListener('pointerleave', () => this.#handleLeave(), { signal: this.signal });

      if (panel) {
        panel.addEventListener('pointerenter', () => this.#cancelClose(), { signal: this.signal });
        panel.addEventListener('pointerleave', () => this.#handleLeave(), { signal: this.signal });
      }

      trigger.addEventListener('click', (e) => {
        if (this.#activeItem === item) return;
        e.preventDefault();
        this.#openItem(item);
      }, { signal: this.signal });
    }

    for (const item of this.querySelectorAll('[data-has-dropdown]')) {
      item.addEventListener('pointerenter', () => this.#handleDropdownEnter(item), { signal: this.signal });
      item.addEventListener('pointerleave', () => this.#handleDropdownLeave(item), { signal: this.signal });
    }

    this.onDocument('click', (e) => {
      if (!this.contains(e.target)) {
        this.#closeActive();
        this.#closeAllDropdowns();
      }
    });

    this.addEventListener('keydown', (e) => this.#handleKeydown(e), { signal: this.signal });

    this.mediaQuery('(min-width: 750px)', (desktop) => {
      if (!desktop) this.#closeActive();
    });
  }

  teardown() {
    clearTimeout(this.#hoverIntent);
    clearTimeout(this.#closeTimer);
    for (const timers of this.#dropdownTimers.values()) {
      clearTimeout(timers.open);
      clearTimeout(timers.close);
    }
    this.#dropdownTimers.clear();
  }

  // --- Megamenu hover ---

  #handleEnter(item) {
    clearTimeout(this.#closeTimer);
    clearTimeout(this.#hoverIntent);
    this.#closeAllDropdowns();
    this.#hoverIntent = setTimeout(() => this.#openItem(item), 150);
  }

  #handleLeave() {
    clearTimeout(this.#hoverIntent);
    this.#closeTimer = setTimeout(() => this.#closeActive(), 300);
  }

  #cancelClose() {
    clearTimeout(this.#closeTimer);
    clearTimeout(this.#hoverIntent);
  }

  #openItem(item) {
    if (this.#activeItem === item) return;
    this.#closeActive();

    const trigger = item.querySelector('[data-megamenu-trigger]');
    if (!trigger) return;

    trigger.setAttribute('aria-expanded', 'true');
    this.#activeItem = item;
    this.classList.add('has-megamenu-open');
  }

  #closeActive() {
    if (!this.#activeItem) return;
    const trigger = this.#activeItem.querySelector('[data-megamenu-trigger]');

    trigger?.setAttribute('aria-expanded', 'false');
    this.classList.remove('has-megamenu-open');
    this.#activeItem = null;
  }

  // --- Simple dropdown hover ---

  #getDropdownTimers(item) {
    if (!this.#dropdownTimers.has(item)) {
      this.#dropdownTimers.set(item, {});
    }
    return this.#dropdownTimers.get(item);
  }

  #handleDropdownEnter(item) {
    this.#closeActive();
    const timers = this.#getDropdownTimers(item);
    clearTimeout(timers.close);

    timers.open = setTimeout(() => {
      this.#closeAllDropdowns();
      const dropdown = item.querySelector('[data-dropdown]');
      const btn = item.querySelector('.header-menu__link--toggle');
      if (!dropdown || !btn) return;
      dropdown.classList.add('is-open');
      btn.setAttribute('aria-expanded', 'true');
    }, 150);
  }

  #handleDropdownLeave(item) {
    const timers = this.#getDropdownTimers(item);
    clearTimeout(timers.open);

    timers.close = setTimeout(() => {
      const dropdown = item.querySelector('[data-dropdown]');
      const btn = item.querySelector('.header-menu__link--toggle');
      dropdown?.classList.remove('is-open');
      btn?.setAttribute('aria-expanded', 'false');
    }, 300);
  }

  #closeAllDropdowns() {
    for (const d of this.querySelectorAll('[data-dropdown].is-open')) {
      d.classList.remove('is-open');
      d.closest('[data-has-dropdown]')?.querySelector('[aria-expanded]')?.setAttribute('aria-expanded', 'false');
    }
  }

  // --- Keyboard ---

  #handleKeydown(e) {
    if (e.key === 'Escape') {
      const hadActive = !!this.#activeItem;
      const trigger = this.#activeItem?.querySelector('[data-megamenu-trigger]');
      this.#closeActive();
      this.#closeAllDropdowns();
      if (hadActive) trigger?.focus();
    }

    if (e.key === 'ArrowDown' && this.#activeItem) {
      const panel = this.#activeItem.querySelector('[data-megamenu-panel]');
      if (!panel) return;
      e.preventDefault();
      const first = panel.querySelector('a, button');
      first?.focus();
    }
  }

  // --- Design mode ---

  onBlockSelect(e) {
    const megamenuItem = e.target?.closest('[data-has-megamenu]');
    if (megamenuItem) this.#openItem(megamenuItem);
  }

  onBlockDeselect() {
    this.#closeActive();
  }
}

if (!customElements.get('scale-megamenu-nav')) {
  customElements.define('scale-megamenu-nav', ScaleMegamenuNav);
}
